import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import PersonaBadge from '../../common/PersonaBadge';

interface Props {
  nodes: any[];
  manifestId: string;
  /** Map of persona ID → display name, resolved by parent */
  personaNames?: Record<string, string>;
}

const PersonaContributions: React.FC<Props> = ({ nodes, personaNames = {} }) => {
  const { darkMode } = useTheme();

  // Group node count by persona
  const personaMap = new Map<string, number>();
  nodes.forEach(n => {
    const pid = n.assigned_persona_id || 'unassigned';
    personaMap.set(pid, (personaMap.get(pid) || 0) + 1);
  });

  const assigned = Array.from(personaMap.entries())
    .filter(([k]) => k !== 'unassigned')
    .sort((a, b) => b[1] - a[1]);

  const unassignedCount = personaMap.get('unassigned') || 0;
  const maxCount = assigned.length > 0 ? assigned[0][1] : 1;

  return (
    <div className="space-y-6">
      {unassignedCount > 0 && (
        <div className={`p-3 rounded-lg border ${darkMode ? 'bg-amber-900/10 border-amber-700/50' : 'bg-amber-50 border-amber-200'}`}>
          <p className="text-sm text-amber-400">
            {unassignedCount} unassigned node{unassignedCount !== 1 ? 's' : ''} — consider assigning personas
          </p>
        </div>
      )}

      <div className={`rounded-lg shadow-lg border-l-4 p-5 ${darkMode ? 'bg-gray-800 border-cyan-600' : 'bg-white border-cyan-500 shadow-sm'}`}>
        <h3 className="text-sm font-semibold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-teal-400">
          Node Distribution by Persona
        </h3>
        {assigned.length === 0 ? (
          <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            No persona assignments yet
          </p>
        ) : (
          <div className="space-y-2.5">
            {assigned.map(([personaId, count]) => {
              const name = personaNames[personaId] || '';
              const pct = nodes.length > 0 ? Math.round((count / nodes.length) * 100) : 0;
              const barPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
              return (
                <div key={personaId} className="flex items-center gap-3">
                  <PersonaBadge personaId={personaId} personaName={name || personaId} size="sm" clickable={false} />
                  <span className={`text-sm truncate w-[200px] shrink-0 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {(name || personaId).replace(/_/g, ' ')}
                  </span>
                  <div className="w-40 shrink-0 h-2.5 bg-gray-700/50 rounded-full overflow-hidden">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 transition-all duration-500"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <span className={`text-xs w-24 text-right tabular-nums shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {count} node{count !== 1 ? 's' : ''} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonaContributions;
